import React from 'react';
import * as Lucide from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { PRODUCT_SUMMARY, DETAILED_SPECS, FEATURES_BENEFITS, IDEAL_FOR } from '../mock';

const LightIcon = ({ name, size = 40, iconSize = 20 }) => {
  const Icon = Lucide[name] || Lucide.Circle;
  return (
    <div
      className="inline-flex items-center justify-center rounded-full bg-[#FFF3E4] text-[#F5901F] shrink-0 transition-colors"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <Icon size={iconSize} strokeWidth={2.2} />
    </div>
  );
};

// Rows that used to live under the (now removed) "Core Specs" category.
// Excluded entirely from the Detailed Feature Specifications display,
// rather than falling through into "Other Specs".
const CORE_SPEC_KEYWORDS = ['communication mode', 'operation', 'weight', 'mounting', 'hands availability'];

// Group the flat DETAILED_SPECS list into a handful of logical categories.
// Matching is keyword-based against row.feature so it keeps working even if
// items are added/removed/reordered in mock.js — no new data field required.
const SPEC_CATEGORIES = [
  {
    id: 'audio',
    label: 'Audio & Comms Quality',
    icon: 'Mic',
    keywords: ['audio clarity', 'full duplex', 'commander communication'],
  },
  {
    id: 'range',
    label: 'Range & Coverage',
    icon: 'Radio',
    keywords: ['booster support', 'range', 'booster coverage', 'enclosed spaces'],
  },
  {
    id: 'deployment',
    label: 'Deployment & Durability',
    icon: 'ShieldCheck',
    keywords: ['infrastructure', 'ip rating', 'suitability for missions'],
  },
];

const groupSpecs = (specs) => {
  const groups = SPEC_CATEGORIES.map((cat) => ({ ...cat, items: [] }));
  const other = { id: 'other', label: 'Other Specs', icon: 'ListPlus', items: [] };

  specs.forEach((row) => {
    const key = (row.feature || '').toLowerCase();
    if (CORE_SPEC_KEYWORDS.some((kw) => key.includes(kw))) return; // drop core specs
    const match = groups.find((g) => g.keywords.some((kw) => key.includes(kw)));
    if (match) match.items.push(row);
    else other.items.push(row);
  });

  const result = groups.filter((g) => g.items.length > 0);
  if (other.items.length > 0) result.push(other);
  return result;
};

const ProductOverviewSection = () => {
  const groupedSpecs = groupSpecs(DETAILED_SPECS);

  return (
    <section id="overview" className="light-section relative py-16 md:py-24">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="reveal-on-scroll">
          <h2 className="font-display font-black text-4xl md:text-6xl leading-tight text-navy mt-2">
            PRODUCT <span className="orange-accent">OVERVIEW</span>
          </h2>
          <div className="text-navy-70 mt-3 text-base">HERTZX&trade; Integrated Hands-Free Communication System</div>
          <div className="mt-4 h-1 w-16 rounded orange-bar" />
        </div>

        <div className="grid grid-cols-1 gap-6 mt-10">
          {/* Product Summary - Stat badge grid */}
          <div className="light-card rounded-xl overflow-hidden reveal-on-scroll self-start p-5">
            <div className="flex items-center gap-3 mb-4">
              <LightIcon name="ClipboardList" size={36} iconSize={18} />
              <span className="font-bold text-navy tracking-wide text-base">PRODUCT SUMMARY</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PRODUCT_SUMMARY.map((row, idx) => (
                <div
                  key={idx}
                  className={`rounded-lg p-3.5 border transition-colors ${
                    row.highlight
                      ? 'bg-[#FFF3E4] border-[#F5901F]/40'
                      : 'bg-[#F6F8FB] border-[#E6EAF0]'
                  }`}
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider text-navy-70 mb-1">
                    {row.attribute}
                  </div>
                  <div
                    className={`text-sm font-semibold leading-snug ${
                      row.highlight ? 'text-[#B65D00]' : 'text-navy'
                    }`}
                  >
                    {row.details}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Specs - Tile grid on desktop, grouped accordion on mobile */}
          <div className="light-card rounded-xl overflow-hidden reveal-on-scroll self-start">
            <div className="px-5 py-4 flex items-center gap-3 border-b border-[#E6EAF0]">
              <LightIcon name="Settings" size={36} iconSize={18} />
              <span className="font-bold text-navy tracking-wide text-base">DETAILED FEATURE SPECIFICATIONS</span>
            </div>

            {/* Desktop: spec tile grid, grouped under category subheadings */}
            <div className="hidden md:block p-5 bg-[#FBFCFE] space-y-6">
              {groupedSpecs.map((group) => (
                <div key={group.id}>
                  <div className="flex items-center gap-2 mb-3">
                    <LightIcon name={group.icon} size={28} iconSize={15} />
                    <span className="text-xs font-bold uppercase tracking-wider text-navy-70">
                      {group.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {group.items.map((row, idx) => (
                      <div
                        key={idx}
                        className="rounded-lg border border-[#E6EAF0] bg-white p-4 hover:border-[#F5901F]/60 hover:shadow-sm transition-all"
                      >
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[#F5901F] mb-1.5">
                          {row.feature}
                        </div>
                        <div className="text-sm text-navy leading-relaxed">
                          {row.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile: category-level accordion (collapsed groups instead of 18 flat rows) */}
            <div className="md:hidden">
              <Accordion type="multiple" className="divide-y divide-[#E6EAF0]">
                {groupedSpecs.map((group) => (
                  <AccordionItem key={group.id} value={group.id} className="border-b-0">
                    <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-[#FFF7EC] [&>svg]:h-4 [&>svg]:w-4 [&>svg]:text-[#F5901F]">
                      <div className="flex items-center gap-3 text-left">
                        <LightIcon name={group.icon} size={32} iconSize={16} />
                        <div className="flex flex-col">
                          <span className="font-semibold text-navy text-sm">{group.label}</span>
                          <span className="text-[11px] text-navy-70">{group.items.length} specs</span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-4 pt-0">
                      <div className="ml-4 rounded-lg bg-[#F6F8FB] divide-y divide-[#E6EAF0] overflow-hidden">
                        {group.items.map((row, idx) => (
                          <div key={idx} className="p-3.5">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-[#F5901F] mb-1">
                              {row.feature}
                            </div>
                            <p className="text-navy-70 text-sm leading-relaxed whitespace-normal">
                              {row.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>

        {/* Ideal For strip - single row of photos, no labels */}
        <div className="mt-12 reveal-on-scroll">
          <div className="flex items-center gap-2 mb-5">
            <div className="h-1 w-8 orange-bar rounded" />
            <span className="font-black text-navy tracking-wider">IDEAL FOR</span>
          </div>
          <div className="flex flex-nowrap gap-2 overflow-x-auto">
            {[
              { label: "FIRE FIGHTING",          img: '/fire_safety.jpeg' },
              { label: "DEFENCE & TACTICAL OPS", img: '/defense.jpeg' },
              { label: "NAVY & COAST GUARD",     img: '/navy.jpeg' },
              { label: "INDUSTRIAL SAFETY",      img: '/industrial_safety.jpeg' },
              { label: "MINING OPERATIONS",      img: '/oil.jpeg' },
              { label: "CORPORATE SECURITY",     img: '/corporate.jpeg' },
              { label: "DISASTER RESPONSE",      img: '/disaster.jpeg' },
            ].map((it) => (
              <div
                key={it.label}
                className="relative aspect-square rounded-md overflow-hidden group flex-1 min-w-[100px]"
              >
                <img
                  src={it.img}
                  alt={it.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ProductOverviewSection;